import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, Menu, Leaf, LayoutDashboard, Heart } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const { totals, setDrawerOpen } = useCart();
  const { user, logout } = useAuth();
  const { count: wishCount } = useWishlist();
  const navigate = useNavigate();

  const linkClass = ({ isActive }) =>
    `text-sm font-medium tracking-tight transition-colors ${isActive ? "text-[#2C3E2D]" : "text-[#6B7B6C] hover:text-[#2C3E2D]"}`;

  return (
    <header className="sticky top-0 z-40 glass-header border-b border-[#E5E5E0]/60" data-testid="app-navbar">
      <div className="mx-auto max-w-7xl px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#2C3E2D] text-[#F9F8F6]">
            <Leaf className="h-4 w-4" strokeWidth={2} />
          </span>
          <span className="font-heading text-lg tracking-tight text-[#2C3E2D]">Gramo<span className="text-[#E07A5F]">.</span>in</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/" end className={linkClass} data-testid="nav-home">Home</NavLink>
          <NavLink to="/shop" className={linkClass} data-testid="nav-shop">Shop</NavLink>
          <NavLink to="/shop?category=fruits" className={linkClass} data-testid="nav-fruits">Fruits</NavLink>
          <NavLink to="/shop?category=vegetables" className={linkClass} data-testid="nav-vegetables">Vegetables</NavLink>
          <NavLink to="/shop?category=grocery" className={linkClass} data-testid="nav-grocery">Grocery</NavLink>
          <NavLink to="/shop?category=restaurant" className={linkClass} data-testid="nav-restaurant">Restaurant</NavLink>
        </nav>

        <div className="flex items-center gap-2">
          {user && (
            <button
              data-testid="nav-wishlist-btn"
              onClick={() => navigate("/wishlist")}
              className="relative inline-flex items-center gap-2 rounded-full border border-[#E5E5E0] bg-white p-2 hover:border-[#81B29A] transition-colors"
              aria-label="wishlist"
            >
              <Heart className="h-4 w-4" strokeWidth={1.75} />
              {wishCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-4 px-1 inline-flex items-center justify-center rounded-full bg-[#E07A5F] text-white text-[10px] font-semibold" data-testid="wishlist-count-badge">{wishCount}</span>
              )}
            </button>
          )}
          <button
            data-testid="open-cart-btn"
            onClick={() => setDrawerOpen(true)}
            className="relative inline-flex items-center gap-2 rounded-full border border-[#E5E5E0] bg-white px-4 py-2 text-sm hover:border-[#81B29A] transition-colors"
          >
            <ShoppingBag className="h-4 w-4" strokeWidth={1.5} />
            <span className="hidden sm:inline text-[#2C3E2D]">Cart</span>
            <span
              data-testid="cart-count-badge"
              className="ml-1 min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-[#E07A5F] text-white text-[11px] font-semibold"
            >{totals.count}</span>
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="inline-flex items-center gap-2 rounded-full border border-[#E5E5E0] bg-white px-3 py-2 text-sm hover:border-[#81B29A] transition-colors"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="h-6 w-6 rounded-full object-cover" />
                  ) : (
                    <User className="h-4 w-4" strokeWidth={1.5} />
                  )}
                  <span className="hidden sm:inline max-w-[100px] truncate">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="text-sm font-medium text-[#2C3E2D]">{user.name}</div>
                  <div className="text-xs text-[#6B7B6C]">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-account">
                  <User className="mr-2 h-4 w-4" /> My Orders
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/wishlist")} data-testid="menu-wishlist">
                  <Heart className="mr-2 h-4 w-4" /> Wishlist
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/addresses")} data-testid="menu-addresses">
                  <LayoutDashboard className="mr-2 h-4 w-4" /> Addresses
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <LayoutDashboard className="mr-2 h-4 w-4" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              data-testid="nav-login-btn"
              onClick={() => navigate("/login")}
              className="btn-primary rounded-full px-5"
            >Sign In</Button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
